import { useTranslation } from 'react-i18next';
import {
  AlgComponentType,
  PreferenceLabel,
  PreferenceSlot,
  PrefLabelId,
  PrefSlotId,
  SectionSlot,
  User,
  UserPreference,
  UserPreferenceId,
  UserPreferenceType,
} from '@youshift/shared/types';
import { ComponentPrioritySchema } from '@youshift/shared/hooks/mutations';
import { Tab } from '@headlessui/react';

import UserBlocksPriorityByUser from './UserBlocksPriorityByUser';
import UserBlocksPriorityByType from './UserBlocksPriorityByType';
import InfoIcon from '../InfoIcon';

const getTypeColor = (type: UserPreferenceType): string => {
  switch (type) {
    case UserPreferenceType.PERSONAL_BLOCKING:
      return 'bg-red-100 text-red-800';
    case UserPreferenceType.JUSTIFIED_BLOCKING:
      return 'bg-yellow-100 text-yellow-800';
    default:
      return 'bg-gray-100 text-gray-800';
  }
};

interface UserBlocksPriorityProps {
  users: Record<number, User>;
  importanceValues: ComponentPrioritySchema;
  setImportanceValues: (
    fn: (prev: ComponentPrioritySchema) => ComponentPrioritySchema,
  ) => void;
  userPreferences: Record<UserPreferenceId, UserPreference>;
  prefLabels: Record<PrefLabelId, PreferenceLabel>;
  preferenceSlots: Record<PrefSlotId, PreferenceSlot>;
  sectionSlotsDict: Record<number, SectionSlot>;
}

export default function UserBlocksPriority({
  users,
  importanceValues,
  setImportanceValues,
  userPreferences,
  prefLabels,
  preferenceSlots,
  sectionSlotsDict,
}: UserBlocksPriorityProps) {
  const { t } = useTranslation();

  return (
    <div className="border rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <h3 className="font-medium">
          {t(
            'manager.smartass.algorithmSettingsModal.userBlocksPriority.title',
          )}
        </h3>
        <InfoIcon
          title={t(
            'manager.smartass.algorithmSettingsModal.userBlocksPriority.title',
          )}
          content={
            <>
              <p className="pt-4">
                {t(
                  'manager.smartass.algorithmSettingsModal.userBlocksPriority.description',
                )}
              </p>
              <p className="mt-2 font-medium">
                {t('manager.smartass.algorithmSettingsModal.constraintCount', {
                  count:
                    Object.values(
                      importanceValues[AlgComponentType.C_PERSONAL_BLOCKING],
                    ).length +
                    Object.values(
                      importanceValues[AlgComponentType.C_JUSTIFIED_BLOCKING],
                    ).length +
                    Object.values(
                      importanceValues[AlgComponentType.C_SPECIAL_EVENTS],
                    ).length,
                })}
              </p>
            </>
          }
          className="w-4 h-4"
          iconSize={6}
          size="2xl"
        />
      </div>
      <div className="flex flex-wrap gap-2 mb-4">
        <div
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getTypeColor(UserPreferenceType.PERSONAL_BLOCKING)}`}
        >
          {t('generic.personalBlock')}
        </div>
        <div
          className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium ${getTypeColor(UserPreferenceType.JUSTIFIED_BLOCKING)}`}
        >
          {t('generic.justifiedBlock')}
        </div>
      </div>
      <Tab.Group>
        <Tab.List className="flex space-x-1 rounded-xl bg-gray-100 p-1 mb-4">
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? 'bg-white text-blue-700 shadow'
                  : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
              }`
            }
          >
            {t('manager.smartass.algorithmSettingsModal.byType')}
          </Tab>
          <Tab
            className={({ selected }) =>
              `w-full rounded-lg py-2.5 text-sm font-medium leading-5
              ${
                selected
                  ? 'bg-white text-blue-700 shadow'
                  : 'text-gray-700 hover:bg-white/[0.12] hover:text-blue-600'
              }`
            }
          >
            {t('manager.smartass.algorithmSettingsModal.byUser')}
          </Tab>
        </Tab.List>
        <Tab.Panels>
          <Tab.Panel>
            <UserBlocksPriorityByType
              users={users}
              importanceValues={importanceValues}
              setImportanceValues={setImportanceValues}
              userPreferences={userPreferences}
              prefLabels={prefLabels}
              preferenceSlots={preferenceSlots}
              sectionSlots={sectionSlotsDict}
              getTypeColor={getTypeColor}
            />
          </Tab.Panel>
          <Tab.Panel>
            <UserBlocksPriorityByUser
              users={users}
              importanceValues={importanceValues}
              setImportanceValues={setImportanceValues}
              userPreferences={userPreferences}
              prefLabels={prefLabels}
              preferenceSlots={preferenceSlots}
              sectionSlots={sectionSlotsDict}
              getTypeColor={getTypeColor}
            />
          </Tab.Panel>
        </Tab.Panels>
      </Tab.Group>
    </div>
  );
}
